import { buildUrl } from "helpers";
import { instance } from "libs/api";

import {
  AiAssistant,
  AiLesson,
  AiLessonDraft,
  AiLessonFirePreview,
  AiLessonListResponse,
  AiLessonReplayVerdict,
  AiLessonTriggers,
  AiLessonVariants,
  EAiLessonCategory,
  EAiLessonStatus
} from "../../aiAgents.types";
import {
  AiAssistantSubRoute,
  ApiRoutesAiAssistants
} from "../../constants/enums";
import { toRepeatedQueryString } from "../../helpers/lessons";

const lessonsBase = (assistantId: string) =>
  buildUrl(
    ApiRoutesAiAssistants.Assistants,
    assistantId,
    AiAssistantSubRoute.Lessons
  );

const lessonUrl = (assistantId: string, lessonId: string, action?: string) =>
  action
    ? buildUrl(lessonsBase(assistantId), lessonId, action)
    : buildUrl(lessonsBase(assistantId), lessonId);

export interface GetLessonsParams {
  assistantId: string;
  statuses?: EAiLessonStatus[];
  search?: string;
  page?: number;
  take?: number;
}

export const requestGetLessons = async ({
  assistantId,
  ...params
}: GetLessonsParams): Promise<AiLessonListResponse> =>
  await instance.get(lessonsBase(assistantId), {
    params,
    // The backend reads repeated params (`statuses=draft&statuses=tested`);
    // axios' default `statuses[]=draft` is dropped by its query parser, and
    // every sub-tab would list every lesson.
    paramsSerializer: { serialize: toRepeatedQueryString }
  });

// L2: one lesson with its full replay result (the list leaves the runs out).
export const requestGetLesson = async ({
  assistantId,
  lessonId
}: {
  assistantId: string;
  lessonId: string;
}): Promise<AiLesson> => await instance.get(lessonUrl(assistantId, lessonId));

export interface CreateLessonBody {
  assistantId: string;
  category: EAiLessonCategory;
  title: string;
  situation?: string;
  badReply?: string;
  correctedReply?: string;
  guidance: string;
  triggers: AiLessonTriggers;
  variants?: AiLessonVariants;
  sourceConversationId?: string;
  sourceTurnMessageId?: string;
  sourceMessageId?: string;
}

export const requestCreateLesson = async ({
  assistantId,
  ...body
}: CreateLessonBody): Promise<AiLesson> =>
  await instance.post(lessonsBase(assistantId), body);

export interface UpdateLessonBody {
  assistantId: string;
  lessonId: string;
  category?: EAiLessonCategory;
  title?: string;
  situation?: string;
  badReply?: string;
  correctedReply?: string;
  guidance?: string;
  triggers?: AiLessonTriggers;
  variants?: AiLessonVariants;
}

export const requestUpdateLesson = async ({
  assistantId,
  lessonId,
  ...body
}: UpdateLessonBody): Promise<AiLesson> =>
  await instance.patch(lessonUrl(assistantId, lessonId), body);

export interface LessonActionBody {
  assistantId: string;
  lessonId: string;
}

export interface ToggleLessonBody extends LessonActionBody {
  isEnabled: boolean;
}

export const requestToggleLesson = async ({
  assistantId,
  lessonId,
  isEnabled
}: ToggleLessonBody): Promise<AiLesson> =>
  await instance.patch(lessonUrl(assistantId, lessonId, "toggle"), {
    isEnabled
  });

export const requestActivateLesson = async ({
  assistantId,
  lessonId
}: LessonActionBody): Promise<AiLesson> =>
  await instance.post(lessonUrl(assistantId, lessonId, "activate"));

export const requestArchiveLesson = async ({
  assistantId,
  lessonId
}: LessonActionBody): Promise<AiLesson> =>
  await instance.post(lessonUrl(assistantId, lessonId, "archive"));

export const requestRestoreLesson = async ({
  assistantId,
  lessonId
}: LessonActionBody): Promise<AiLesson> =>
  await instance.post(lessonUrl(assistantId, lessonId, "restore"));

export interface DraftLessonBody {
  assistantId: string;
  conversationId: string;
  turnMessageId: string;
  badReply?: string;
  // The admin's own words on what should change.
  correction?: string;
}

export const requestDraftLesson = async ({
  assistantId,
  ...body
}: DraftLessonBody): Promise<AiLessonDraft> =>
  await instance.post(
    buildUrl(
      ApiRoutesAiAssistants.Assistants,
      assistantId,
      AiAssistantSubRoute.LessonDraftAssist
    ),
    body
  );

export interface LessonFirePreviewParams {
  assistantId: string;
  lessonId: string;
  days?: number;
}

export const requestLessonFirePreview = async ({
  assistantId,
  lessonId,
  days
}: LessonFirePreviewParams): Promise<AiLessonFirePreview> =>
  await instance.get(lessonUrl(assistantId, lessonId, "fire-preview"), {
    params: { days }
  });

export interface ReplayLessonBody {
  assistantId: string;
  lessonId: string;
  // 1 to 3 replays, each with the lesson and without it.
  runs: number;
}

// R1: synchronous, it answers once every run is over (usually under a
// minute, at most a few). The server writes a "running" marker first, so a
// reload in the meantime still shows the replay as running.
export const requestReplayLesson = async ({
  assistantId,
  lessonId,
  runs
}: ReplayLessonBody): Promise<AiLesson> =>
  await instance.post(lessonUrl(assistantId, lessonId, "replay"), { runs });

export interface SetReplayVerdictBody {
  assistantId: string;
  lessonId: string;
  verdict: AiLessonReplayVerdict;
}

// R2: "passed" is what lets a non-Super admin activate the lesson.
export const requestSetReplayVerdict = async ({
  assistantId,
  lessonId,
  verdict
}: SetReplayVerdictBody): Promise<AiLesson> =>
  await instance.patch(lessonUrl(assistantId, lessonId, "replay-verdict"), {
    verdict
  });

export interface SetLessonsEnabledBody {
  assistantId: string;
  lessonsEnabled: boolean;
}

export const requestSetLessonsEnabled = async ({
  assistantId,
  lessonsEnabled
}: SetLessonsEnabledBody): Promise<AiAssistant> =>
  await instance.patch(
    buildUrl(
      ApiRoutesAiAssistants.Assistants,
      assistantId,
      AiAssistantSubRoute.LessonsEnabled
    ),
    { lessonsEnabled }
  );
